import { useState, useEffect } from "react";
import { useAppDispatch } from "../../app/hook";
import { useParams, useNavigate } from "react-router-dom";
import {
  formatDate,
  formatDateHyphen,
  formatYearDate,
} from "../../utils/format";
import {
  SpaceBetween,
  Form,
  Container,
  Header,
  Badge,
} from "@cloudscape-design/components-themed";
import { addNotification } from "../../features/error/notifications-slice";
import { Messages, getMessage } from "../../features/error/messages";
import { LABELS } from "./constants";
import {
  COMMON_LABELS,
  ApprovalStatusPattern,
  ActionPattern,
  DivisionDict,
  getDivisionItems,
  getDivisionDict,
} from "../default-constants";
import { Buttons } from "./Buttons";
import { HelpPanel } from "./HelpPanel";
import { FormBlock1, FormBlock2, FormBlock3, Comment } from "./Forms";
import { openHelpPanel } from "../layouts/help-panel-slice";
import { isNull, isLengthValid } from "../../utils/inputValidation";
import {
  usePutMerchantTypeMutation,
  usePostMerchantTypeMutation,
  useDeleteMerchantTypeMutation,
  useGetCommercialMerchantTypeQuery,
  useGetDivisionQuery,
} from "../../features/api";
import {
  MerchantTypeResist,
  MerchantType,
  MerchantTypeResistUpdate,
  Division,
} from "../../features/api-interfaces";

export const MerchantTypeForm = (props: any) => {
  const dispatch = useAppDispatch();
  const navigator = useNavigate();
  const [postMerchantType] = usePostMerchantTypeMutation();
  const [putMerchantType] = usePutMerchantTypeMutation();
  const [deleteMerchantType] = useDeleteMerchantTypeMutation();
  const { no } = useParams();
  // 各入力項目
  const [applicationStartDate, setApplicationStartDate] = useState("");
  const [typeNo, setTypeNo] = useState<number>();
  const [typeName, setTypeName] = useState("");
  const [addressManagementRequiredFlag, setAddressManagementRequiredFlag] =
    useState(true);
  const [storeInfoAggregationTargetFlag, setStoreInfoAggregationTargetFlag] =
    useState(false);
  const [fakeIndustryCheckRequiredFlag, setFakeIndustryCheckRequiredFlag] =
    useState(true);
  const [storeApplicationFormKbn, setStoreApplicationFormKbn] =
    useState<DivisionDict>({
      label: "",
      value: "",
    });
  const [tenantExaminationRequiredFlag, setTenantExaminationRequiredFlag] =
    useState(true);
  const [tenantExaminationUnitKbn, setTenantExaminationUnitKbn] =
    useState<DivisionDict>({
      label: "",
      value: "",
    });
  const [actionComment, setActionComment] = useState("");
  const [comment, setComment] = useState("");
  const [changeFlag, setChangeFlag] = useState(false);
  //変数宣言
  let updatedAt = "";
  let updatedBy = "";
  let actionAt = "";
  let actionBy = "";
  // 選択肢
  const necessaryItems = [
    { value: true, label: LABELS.NECESSARY },
    { value: false, label: LABELS.UNNECESSARY },
  ];
  const targetItems = [
    { value: true, label: LABELS.TARGET },
    { value: false, label: LABELS.NOT_TARGET },
  ];
  const disabledTargetItems = [
    { value: true, label: LABELS.TARGET, disabled: true },
    { value: false, label: LABELS.NOT_TARGET, disabled: true },
  ];
  const [storeInfoAggregationTargetItems, setStoreInfoAggregationTargetItems] =
    useState([{}]);
  //入力チェックエラーメッセージ
  const [applicationStartDateErrorMsg, setApplicationStartDateErrorMsg] =
    useState("");
  const [typeNameErrorMsg, setTypeNameErrorMsg] = useState("");
  const [storeApplicationFormErrorMsg, setStoreApplicationFormErrorMsg] =
    useState("");
  const [tenantExaminationUnitErrorMsg, setTenantExaminationUnitErrorMsg] =
    useState("");
  const [commentErrorMsg, setCommentErrorMsg] = useState("");
  // データ管理用
  const [branchNo, setBranchNo] = useState<number>();
  const [approvalFlowNo, setApprovalFlowNo] = useState<number>();
  const [approvalStatus, setApprovalStatus] = useState<number>(0);
  const [readOnly, setReadOnly] = useState(false);
  const [merchantTypeNo, setMerchantTypeNo] = useState<string>("");

  // 区分取得API呼び出し(親区分コード：1:店舗申請様式)
  const divisionData1 = useGetDivisionQuery("1");
  const storeApplicationFormDivisions = divisionData1?.data as Division[];
  const storeApplicationFormItems = getDivisionItems(
    storeApplicationFormDivisions
  );
  // 区分取得API呼び出し(親区分コード：2:店子審査単位)
  const divisionData2 = useGetDivisionQuery("2");
  const tenantExaminationUnitDivisions = divisionData2?.data as Division[];
  const tenantExaminationUnitItems = getDivisionItems(
    tenantExaminationUnitDivisions
  );

  // データをセット
  const registeredData: MerchantType = props.registeredData;

  // 商用反映済み加盟店種別情報取得API呼び出し
  let registeredTypeNo: number = 0;
  if (registeredData) {
    registeredTypeNo = registeredData.typeNo;
  }
  const { data: data2, refetch: commercialDataRefetch } =
    useGetCommercialMerchantTypeQuery(registeredTypeNo);
  const commercialData = data2 as MerchantType;

  useEffect(() => {
    // 登録済みのデータを表示する
    if (registeredData !== undefined) {
      const merchantTypeNo = no as string;
      setMerchantTypeNo(merchantTypeNo);
      setApplicationStartDate(
        formatDateHyphen(registeredData.applicationStartDate)
      );
      setTypeNo(registeredData.typeNo);
      setTypeName(registeredData.typeName);
      setAddressManagementRequiredFlag(
        registeredData.addressManagementRequiredFlag
      );
      setStoreInfoAggregationTargetFlag(
        registeredData.storeInfoAggregationTargetFlag
      );
      setFakeIndustryCheckRequiredFlag(
        registeredData.fakeIndustryCheckRequiredFlag
      );
      setStoreApplicationFormKbn(
        getDivisionDict(
          storeApplicationFormItems,
          registeredData.storeApplicationFormKbn
        )
      );
      setTenantExaminationRequiredFlag(
        registeredData.tenantExaminationRequiredFlag
      );
      setTenantExaminationUnitKbn(
        getDivisionDict(
          tenantExaminationUnitItems,
          registeredData.tenantExaminationUnitKbn
        )
      );
      setActionComment(registeredData.actionComment);
      setBranchNo(registeredData.branchNo);
      setApprovalFlowNo(registeredData.approvalFlowNo);
      setApprovalStatus(registeredData.approvalStatus);
      switchReadOnly(registeredData.approvalStatus);
      switchStoreInfoAggregationTarget(
        registeredData.addressManagementRequiredFlag
      );
    } else {
      switchStoreInfoAggregationTarget(addressManagementRequiredFlag);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredData]);

  if (registeredData !== undefined) {
    updatedAt = formatYearDate(registeredData.updatedAt);
    updatedBy = registeredData.updatedBy;
    actionAt = formatYearDate(registeredData.actionAt);
    actionBy = registeredData.actionBy;
  }

  // バリデーションチェック
  // 適用開始日バリデーションチェック
  const checkApplicationStartDate = (applicationStartDate: string): boolean => {
    if (isNull(applicationStartDate)) {
      setApplicationStartDateErrorMsg(
        getMessage("KGRERR023", LABELS.APPLICABLE_START_DATE).label
      );
      return false;
    } else {
      setApplicationStartDateErrorMsg("");
      return true;
    }
  };
  // 種別名バリデーションチェック
  const checkTypeName = (typeName: string): boolean => {
    if (isNull(typeName)) {
      setTypeNameErrorMsg(getMessage("KGRERR023", LABELS.TYPE_NAME).label);
      return false;
    } else if (!isLengthValid(typeName, 100)) {
      setTypeNameErrorMsg(getMessage("KGRERR024", LABELS.TYPE_NAME, 100).label);
      return false;
    } else {
      setTypeNameErrorMsg("");
      return true;
    }
  };
  // 店舗申請様式バリデーションチェック
  const checkStoreApplicationFormKbn = (
    storeApplicationFormKbn: DivisionDict
  ): boolean => {
    setStoreApplicationFormKbn(storeApplicationFormKbn);
    if (storeApplicationFormKbn.value === "") {
      setStoreApplicationFormErrorMsg(
        getMessage("KGRERR023", LABELS.STORE_APPLICATION_FORM).label
      );
      return false;
    } else {
      setStoreApplicationFormErrorMsg("");
      return true;
    }
  };
  // 店子申請単位バリデーションチェック
  const checkTenantExaminationUnit = (
    tenantExaminationRequiredFlag: boolean,
    tenantExaminationUnitKbn: DivisionDict
  ): boolean => {
    if (
      tenantExaminationRequiredFlag === true &&
      tenantExaminationUnitKbn.value === ""
    ) {
      setTenantExaminationUnitErrorMsg(
        getMessage("KGRERR023", LABELS.TENANT_EXAMINATION_UNIT).label
      );
      return false;
    } else {
      setTenantExaminationUnitErrorMsg("");
      return true;
    }
  };
  // コメントバリデーションチェック
  const checkComment = (comment: string): boolean => {
    if (!isLengthValid(comment, 2000)) {
      setCommentErrorMsg(getMessage("KGRERR024", LABELS.COMMENT, 2000).label);
      return false;
    } else {
      setCommentErrorMsg("");
      return true;
    }
  };

  // 差し戻し時のバリデーションチェック
  const checkWhenSendBack = (): boolean => {
    if (isNull(comment)) {
      const messageId = "KGRERR029" as keyof Messages;
      // アラート処理
      dispatch(
        addNotification({
          msgId: messageId,
          msgArgs: [COMMON_LABELS.BTNS.SEND_BACK, LABELS.COMMENT],
        })
      );
      return false;
    } else {
      return true;
    }
  };

  // 入力制御
  const switchReadOnly = (approvalStatus: number) => {
    if (
      approvalStatus === ApprovalStatusPattern.beforeApproval.statusCode ||
      approvalStatus ===
        ApprovalStatusPattern.commercialApplication.statusCode ||
      approvalStatus === 0
    ) {
      setReadOnly(false);
    } else {
      setReadOnly(true);
    }
  };

  // 店舗情報集約入力制御
  const switchStoreInfoAggregationTarget = (flag: boolean) => {
    if (flag === true) {
      // 「住所管理要否」が「要」の場合、「対象外」を設定し変更不可とする
      setStoreInfoAggregationTargetFlag(false);
      setStoreInfoAggregationTargetItems(disabledTargetItems);
    } else {
      setStoreInfoAggregationTargetItems(targetItems);
    }
  };

  // 適用開始日入力
  const onChangeApplicationStartDate = (applicationStartDate: string) => {
    setApplicationStartDate(applicationStartDate);
    checkApplicationStartDate(applicationStartDate);
    setChangeFlag(true);
  };

  // 種別名入力
  const onChangeTypeName = (typeName: string) => {
    setTypeName(typeName);
    checkTypeName(typeName);
    setChangeFlag(true);
  };

  // 店舗申請様式入力
  const onChangeStoreApplicationFormKbn = (
    storeApplicationForm: DivisionDict
  ) => {
    setStoreApplicationFormKbn(storeApplicationForm);
    checkStoreApplicationFormKbn(storeApplicationForm);
    setChangeFlag(true);
  };

  // 店舗情報集約入力
  const onChangeAddressManagement = (
    addressManagementRequiredFlag: boolean
  ) => {
    setAddressManagementRequiredFlag(addressManagementRequiredFlag);
    switchStoreInfoAggregationTarget(addressManagementRequiredFlag);
    setChangeFlag(true);
  };
  // 店子審査要否入力
  const onChangeTenantExaminationRequiredFlag = (
    tenantExaminationRequiredFlag: boolean
  ) => {
    setTenantExaminationRequiredFlag(tenantExaminationRequiredFlag);
    checkTenantExaminationUnit(
      tenantExaminationRequiredFlag,
      tenantExaminationUnitKbn
    );
    setChangeFlag(true);
  };
  // 店子審査単位入力
  const onChangeTenantExaminationUnit = (
    tenantExaminationUnitKbn: DivisionDict
  ) => {
    setTenantExaminationUnitKbn(tenantExaminationUnitKbn);
    checkTenantExaminationUnit(
      tenantExaminationRequiredFlag,
      tenantExaminationUnitKbn
    );
    setChangeFlag(true);
  };
  // コメント入力
  const onChangeComment = (comment: string) => {
    setComment(comment);
    checkComment(comment);
    setChangeFlag(true);
  };

  const getMerchantTypeResist = (
    type: string,
    approvalAction: number
  ): MerchantTypeResist => {
    const merchantTypeResist: MerchantTypeResist = {
      typeName: typeName,
      addressManagementRequiredFlag: addressManagementRequiredFlag,
      storeInfoAggregationTargetFlag: storeInfoAggregationTargetFlag,
      fakeIndustryCheckRequiredFlag: fakeIndustryCheckRequiredFlag,
      storeApplicationFormKbn: String(storeApplicationFormKbn.value),
      tenantExaminationRequiredFlag: tenantExaminationRequiredFlag,
      tenantExaminationUnitKbn: String(tenantExaminationUnitKbn.value),
      applicationStartDate: formatDate(applicationStartDate),
      approvalStatus: approvalStatus,
      approvalAction: approvalAction,
      actionComment: comment,
    };
    if (type === "register") {
      return {
        ...merchantTypeResist,
      };
    } else if (type === "registerUpdate") {
      return {
        ...merchantTypeResist,
        typeNo: typeNo,
        branchNo: branchNo,
        approvalStatus: 0,
      };
    } else {
      return {
        ...merchantTypeResist,
        typeNo: typeNo,
        branchNo: branchNo,
        approvalFlowNo: approvalFlowNo,
      };
    }
  };

  // 加盟店種別情報登録API呼び出し
  const register = (approvalAction: number) => {
    const merchantTypeResist = getMerchantTypeResist(
      "register",
      approvalAction
    );
    postMerchantType(merchantTypeResist).then((result: any) => {
      if (!("error" in result)) {
        navigator(`/merchant-types/${result.data.merchantTypeNo}`);
      }
    });
  };

  // 加盟店種別情報登録API呼び出し(バージョン更新時)
  const registerUpdate = (approvalAction: number) => {
    const merchantTypeResist = getMerchantTypeResist(
      "registerUpdate",
      approvalAction
    );
    postMerchantType(merchantTypeResist).then((result: any) => {
      if (!("error" in result)) {
        navigator(`/merchant-types/${result.data.merchantTypeNo}`);
      }
    });
  };

  // 加盟店種別情報更新API呼び出し
  const update = (approvalAction: number, url: string) => {
    const merchantTypeResist = getMerchantTypeResist("update", approvalAction);
    const data: MerchantTypeResistUpdate = {
      merchantTypeNo: merchantTypeNo,
      data: merchantTypeResist,
    };
    putMerchantType(data).then((result: any) => {
      if (!("error" in result)) {
        props.refetch();
        commercialDataRefetch();
        navigator(url);
      }
    });
  };

  // 加盟店種別情報削除API呼び出し
  const deleteSave = (approvalAction: number) => {
    const merchantTypeResist = getMerchantTypeResist(
      "deleteSave",
      approvalAction
    );
    const data: MerchantTypeResistUpdate = {
      merchantTypeNo: merchantTypeNo,
      data: merchantTypeResist,
    };
    deleteMerchantType(data).then((result: any) => {
      if (!("error" in result)) {
        navigator("/merchant-types");
      }
    });
  };

  // バリデーションチェック
  const checkInput = (): boolean => {
    let flag = true;
    const checks = [
      checkApplicationStartDate(applicationStartDate),
      checkTypeName(typeName),
      checkStoreApplicationFormKbn(storeApplicationFormKbn),
      checkTenantExaminationUnit(
        tenantExaminationRequiredFlag,
        tenantExaminationUnitKbn
      ),
      checkComment(comment),
    ];
    checks.forEach(function (value) {
      if (value === false) {
        flag = false;
      }
    });
    return flag;
  };

  // ボタン押下時の処理
  // 一時保存ボタン
  const onClickTemporarilySaveBtn = () => {
    if (checkInput()) {
      if (merchantTypeNo === "") {
        // 初期登録時
        register(ActionPattern.temporarilySave.statusCode);
      } else if (
        approvalStatus ===
          ApprovalStatusPattern.commercialApplication.statusCode &&
        typeNo !== undefined
      ) {
        // バージョン更新時
        registerUpdate(ActionPattern.temporarilySave.statusCode);
      } else {
        // 更新時
        update(
          ActionPattern.temporarilySave.statusCode,
          `/merchant-types/${merchantTypeNo}`
        );
      }
    }
  };
  // 承認依頼ボタン
  const onClickApprovalRequestBtn = () => {
    if (checkInput()) {
      update(ActionPattern.approvalRequest.statusCode, `/merchant-types/`);
    }
  };
  // 一時保存削除ボタン
  const onClickDeleteTemporarilySaveBtn = () => {
    deleteSave(ActionPattern.delete.statusCode);
  };
  // 承認ボタン
  const onClickApprovalBtn = () => {
    update(ActionPattern.approval.statusCode, `/merchant-types/`);
  };
  // 差戻ボタン
  const onClickSendBackBtn = () => {
    if (checkWhenSendBack()) {
      update(ActionPattern.sendBack.statusCode, `/merchant-types/`);
    }
  };
  // 引戻ボタン（承認前）
  const onClickPullBackBtn = () => {
    update(
      ActionPattern.pullBack.statusCode,
      `/merchant-types/${merchantTypeNo}`
    );
  };
  // 引戻ボタン（承認済み）
  const onClickApprovedPullBackBtn = () => {
    update(
      ActionPattern.approvedPullBack.statusCode,
      `/merchant-types/${merchantTypeNo}`
    );
  };

  const onClickCommercialApplicationInfo = function () {
    //　商用反映済情報
    dispatch(
      openHelpPanel({
        header: <div>{LABELS.COMMERCIAL_APPLICATION_INFO}</div>,
        content: (
          <HelpPanel
            typeNo={typeNo}
            commercialData={commercialData}
            necessaryItems={necessaryItems}
            storeApplicationFormItems={storeApplicationFormItems}
            storeInfoAggregationTargetItems={storeInfoAggregationTargetItems}
            tenantExaminationUnitItems={tenantExaminationUnitItems}
          ></HelpPanel>
        ),
      })
    );
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form>
        <SpaceBetween direction="vertical" size="l">
          <Container
            header={
              <Header
                variant="h3"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    {registeredData ? (
                      <Badge color="green">
                        {LABELS.LAST_UPDATE_AT}
                        {updatedBy}/{LABELS.LAST_UPDATE_BY}
                        {updatedAt}
                      </Badge>
                    ) : (
                      <></>
                    )}
                    {registeredData &&
                    (registeredData.approvalStatus ===
                      ApprovalStatusPattern.afterApproval.statusCode ||
                      registeredData.approvalStatus ===
                        ApprovalStatusPattern.commercialApplication
                          .statusCode) ? (
                      <Badge color="green">
                        {LABELS.LAST_ACTION_AT}
                        {actionBy}/{LABELS.LAST_ACTION_BY}
                        {actionAt}
                      </Badge>
                    ) : (
                      <></>
                    )}
                  </SpaceBetween>
                }
              ></Header>
            }
          >
            <FormBlock1
              onChangeApplicationStartDate={onChangeApplicationStartDate}
              applicationStartDateErrorMsg={applicationStartDateErrorMsg}
              applicationStartDate={applicationStartDate}
              setTypeNo={setTypeNo}
              typeNo={typeNo}
              onChangeTypeName={onChangeTypeName}
              typeNameErrorMsg={typeNameErrorMsg}
              typeName={typeName}
              onChangeAddressManagement={onChangeAddressManagement}
              addressManagementRequiredFlag={addressManagementRequiredFlag}
              necessaryItems={necessaryItems}
              readOnly={readOnly}
              commercialData={commercialData}
              onClickCommercialApplicationInfo={
                onClickCommercialApplicationInfo
              }
            />
          </Container>
          <Container
            header={<Header variant="h3">{LABELS.STORE_SETTING}</Header>}
          >
            <FormBlock2
              setStoreInfoAggregationTargetFlag={
                setStoreInfoAggregationTargetFlag
              }
              storeInfoAggregationTargetFlag={storeInfoAggregationTargetFlag}
              storeInfoAggregationTargetItems={storeInfoAggregationTargetItems}
              setFakeIndustryCheckRequiredFlag={
                setFakeIndustryCheckRequiredFlag
              }
              fakeIndustryCheckRequiredFlag={fakeIndustryCheckRequiredFlag}
              storeApplicationFormKbn={storeApplicationFormKbn}
              onChangeStoreApplicationForm={onChangeStoreApplicationFormKbn}
              storeApplicationFormErrorMsg={storeApplicationFormErrorMsg}
              storeApplicationFormItems={storeApplicationFormItems}
              necessaryItems={necessaryItems}
              targetItems={targetItems}
              readOnly={readOnly}
              setChangeFlag={setChangeFlag}
            />
          </Container>
          <Container
            header={<Header variant="h3">{LABELS.REVIEW_SETTING}</Header>}
          >
            <FormBlock3
              setTenantExaminationRequiredFlag={
                onChangeTenantExaminationRequiredFlag
              }
              tenantExaminationRequiredFlag={tenantExaminationRequiredFlag}
              tenantExaminationUnitKbn={tenantExaminationUnitKbn}
              onChangeTenantExaminationUnit={onChangeTenantExaminationUnit}
              tenantExaminationUnitErrorMsg={tenantExaminationUnitErrorMsg}
              tenantExaminationUnitItems={tenantExaminationUnitItems}
              necessaryItems={necessaryItems}
              readOnly={readOnly}
            />
          </Container>
          {approvalStatus !==
          ApprovalStatusPattern.commercialApplication.statusCode ? (
            <Comment
              actionComment={actionComment}
              comment={comment}
              commentErrorMsg={commentErrorMsg}
              onChangeComment={onChangeComment}
            />
          ) : undefined}
          <Buttons
            changeFlag={changeFlag}
            approvalStatus={approvalStatus}
            onClickTemporarilySaveBtn={onClickTemporarilySaveBtn}
            onClickApprovalRequestBtn={onClickApprovalRequestBtn}
            onClickDeleteTemporarilySaveBtn={onClickDeleteTemporarilySaveBtn}
            onClickApprovalBtn={onClickApprovalBtn}
            onClickSendBackBtn={onClickSendBackBtn}
            onClickPullBackBtn={onClickPullBackBtn}
            onClickApprovedPullBackBtn={onClickApprovedPullBackBtn}
          />
        </SpaceBetween>
      </Form>
    </form>
  );
};
